import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

const Experience = () => {
  const { workExperience, internshipsAndLearning, achievements } = useSelector(state => state.experience);
  const [activeTab, setActiveTab] = useState('work');
  const [expandedItems, setExpandedItems] = useState({});
  
  useEffect(() => {
    document.title = "Experience | Musa Yusuf Magoban";
    window.scrollTo(0, 0);
  }, []);

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const getExperienceTypeColor = (type) => {
    const colorMap = {
      'Full-time': 'bg-green-100 text-green-800 border-green-200',
      'Part-time': 'bg-blue-100 text-blue-800 border-blue-200',
      'Contract': 'bg-purple-100 text-purple-800 border-purple-200',
      'Volunteer': 'bg-orange-100 text-orange-800 border-orange-200',
      'Current': 'bg-green-100 text-green-800 border-green-200',
      'Virtual': 'bg-blue-100 text-blue-800 border-blue-200',
      'Bootcamp': 'bg-purple-100 text-purple-800 border-purple-200',
      'In-Person': 'bg-indigo-100 text-indigo-800 border-indigo-200'
    };
    return colorMap[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const experienceStats = [
    { number: workExperience.length, label: "Work Roles", icon: "Briefcase", color: "primary" },
    { number: internshipsAndLearning.length, label: "Learning Programs", icon: "GraduationCap", color: "secondary" },
    { number: achievements.length, label: "Key Achievements", icon: "Trophy", color: "accent" },
    { number: "2+", label: "Years Experience", icon: "Clock", color: "primary" }
  ];

  const tabs = [
    { id: 'work', label: 'Work Experience', icon: 'Briefcase' },
    { id: 'learning', label: 'Learning & Growth', icon: 'GraduationCap' },
    { id: 'achievements', label: 'Achievements', icon: 'Trophy' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Experience | Musa Yusuf Magoban</title>
        <meta name="description" content="Professional experience in cybersecurity, including roles at TekHaven Solutions, CyberAdnation, and various security internships." />
      </Helmet>

      <Header />

      <main className="pt-16">
        {/* Enhanced Header Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-secondary/5 to-accent/5 relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2 bg-accent/10 rounded-full text-accent font-medium text-sm mb-6">
                <Icon name="TrendingUp" size={16} className="mr-2" />
                Career Journey
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
                Professional <span className="text-primary">Experience</span>
              </h1>
              <p className="text-xl text-text-secondary max-w-4xl mx-auto leading-relaxed">
                Building practical <span className="text-primary font-medium">cybersecurity expertise</span> through 
                hands-on roles, <span className="text-secondary font-medium">internships</span>, and 
                <span className="text-accent font-medium">continuous learning</span> initiatives.
              </p>
              
              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 max-w-4xl mx-auto">
                {experienceStats.map((stat, index) => (
                  <div key={index} className="bg-surface/80 backdrop-blur-sm rounded-xl p-4 security-shadow hover:security-shadow-strong transition-all duration-300 transform hover:-translate-y-1">
                    <div className={`w-10 h-10 bg-${stat.color}/10 rounded-lg flex items-center justify-center mx-auto mb-2`}>
                      <Icon name={stat.icon} size={20} className={`text-${stat.color}`} />
                    </div>
                    <div className="text-2xl font-bold text-foreground">{stat.number}</div>
                    <div className="text-sm text-text-secondary">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Tab Navigation */}
        <section className="py-8 bg-surface/50 border-b border-border/50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center">
              <div className="inline-flex bg-muted rounded-lg p-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      activeTab === tab.id
                        ? 'bg-primary text-primary-foreground shadow-lg'
                        : 'text-text-secondary hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab.icon} size={16} className="mr-2 inline" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Work Experience */}
        {activeTab === 'work' && (
          <section className="py-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Professional Roles
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  Building expertise through diverse cybersecurity roles and responsibilities
                </p>
              </div>
              
              <div className="space-y-8">
                {workExperience.map((exp) => (
                  <div key={exp.id} className="group bg-surface rounded-xl p-8 security-shadow hover:security-shadow-strong transition-all duration-300 transform hover:-translate-y-1 border border-border/50 hover:border-primary/20 relative overflow-hidden">
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                    
                    <div className="relative z-10">
                      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                        <div className="flex-1">
                          <h3 className="text-2xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors duration-300">
                            {exp.role}
                          </h3>
                          <p className="text-xl text-primary font-medium mb-2">
                            {exp.company}
                          </p>
                          <p className="text-text-secondary font-medium">
                            {exp.period}
                          </p>
                        </div>
                        <div className="flex items-center space-x-4 mt-4 lg:mt-0">
                          <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getExperienceTypeColor(exp.type)}`}>
                            <div className="w-2 h-2 bg-current rounded-full mr-2"></div>
                            {exp.type}
                          </span>
                          <button
                            onClick={() => toggleExpanded(exp.id)}
                            className="p-2 text-text-secondary hover:text-primary rounded-lg hover:bg-primary/10 transition-colors"
                          >
                            <Icon name={expandedItems[exp.id] ? "ChevronUp" : "ChevronDown"} size={20} />
                          </button>
                        </div>
                      </div>
                      
                      {/* Achievements */}
                      <div className={`transition-all duration-300 ${expandedItems[exp.id] ? 'max-h-none opacity-100' : 'max-h-40 overflow-hidden'}`}>
                        <div className="border-t border-border/30 pt-6">
                          <h4 className="text-lg font-medium text-foreground mb-4">Key Achievements</h4>
                          <div className="grid md:grid-cols-2 gap-4">
                            {exp.achievements.map((achievement, achIndex) => (
                              <div key={achIndex} className="flex items-start group/achievement">
                                <div className="w-6 h-6 bg-accent/10 rounded-full flex items-center justify-center mt-0.5 mr-3 flex-shrink-0 group-hover/achievement:bg-accent/20 transition-colors">
                                  <Icon name="CheckCircle" size={14} className="text-accent" />
                                </div>
                                <span className="text-text-secondary group-hover/achievement:text-foreground transition-colors leading-relaxed">
                                  {achievement}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      {!expandedItems[exp.id] && exp.achievements.length > 4 && (
                        <div className="mt-4 text-center">
                          <button
                            onClick={() => toggleExpanded(exp.id)}
                            className="text-primary hover:text-primary/80 text-sm font-medium"
                          >
                            View all {exp.achievements.length} achievements
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Internships & Learning */}
        {activeTab === 'learning' && (
          <section className="py-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Learning & Growth
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  Continuous development through internships, bootcamps, and specialized training
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {internshipsAndLearning.map((item) => (
                  <div key={item.id} className="group bg-surface rounded-xl p-6 security-shadow hover:security-shadow-strong transition-all duration-300 transform hover:-translate-y-1 border border-border/50 hover:border-secondary/20 relative overflow-hidden">
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                    
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-secondary/10 to-accent/10 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <Icon name="GraduationCap" size={24} className="text-secondary group-hover:text-accent transition-colors duration-300" />
                        </div>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getExperienceTypeColor(item.type)}`}>
                          <div className="w-2 h-2 bg-current rounded-full mr-2"></div>
                          {item.type}
                        </span>
                      </div>
                      
                      <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-secondary transition-colors duration-300">
                        {item.title}
                      </h3>
                      
                      <p className="text-secondary font-medium mb-2">
                        {item.organization}
                      </p>
                      
                      <p className="text-text-secondary text-sm mb-4">
                        {item.period}
                      </p>
                      
                      <div className="border-t border-border/30 pt-4">
                        <p className="text-text-secondary leading-relaxed line-clamp-3">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Achievements */}
        {activeTab === 'achievements' && (
          <section className="py-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-foreground mb-4">
                  Key Achievements
                </h2>
                <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                  Recognition and accomplishments throughout my cybersecurity journey
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {achievements.map((achievement) => (
                  <div key={achievement.id} className="group bg-surface rounded-xl p-6 security-shadow hover:security-shadow-strong transition-all duration-300 transform hover:-translate-y-1 border border-border/50 hover:border-accent/20 relative overflow-hidden">
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-accent/5 to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>
                    
                    <div className="relative z-10">
                      <div className="w-12 h-12 bg-gradient-to-br from-accent/10 to-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        <Icon name={achievement.icon} size={24} className="text-accent group-hover:text-primary transition-colors duration-300" />
                      </div>
                      
                      <h3 className="text-lg font-semibold text-foreground mb-3 group-hover:text-accent transition-colors duration-300">
                        {achievement.title}
                      </h3>
                      
                      <p className="text-text-secondary leading-relaxed">
                        {achievement.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Career Timeline */}
        <section className="py-20 bg-gradient-to-br from-muted/30 to-muted/10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full text-primary font-medium text-sm mb-6">
                <Icon name="Clock" size={16} className="mr-2" />
                Career Progression
              </div>
              <h2 className="text-3xl font-bold text-foreground mb-4">
                Professional Timeline
              </h2>
              <p className="text-lg text-text-secondary max-w-2xl mx-auto">
                A chronological overview of my career development and growth
              </p>
            </div>
            
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gradient-to-b from-primary to-secondary"></div>
              
              <div className="space-y-12">
                {[
                  { year: "2023", title: "Cybersecurity Transition", description: "Started professional cybersecurity journey with certifications and virtual internships", icon: "Shield" },
                  { year: "2024", title: "Hands-On Experience", description: "Built SOC lab, completed security projects, and gained practical experience", icon: "Code" },
                  { year: "2025", title: "Professional Growth", description: "Pursuing advanced certifications and expanding technical expertise", icon: "TrendingUp" }
                ].map((item, index) => (
                  <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                    <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                      <div className="bg-surface rounded-xl p-6 security-shadow hover:security-shadow-strong transition-all duration-300 transform hover:-translate-y-1">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-lg font-semibold text-foreground">{item.title}</h3>
                          <span className="text-sm font-medium text-primary">{item.year}</span>
                        </div>
                        <p className="text-text-secondary">{item.description}</p>
                      </div>
                    </div>
                    
                    <div className="relative z-10">
                      <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full flex items-center justify-center border-4 border-surface">
                        <Icon name={item.icon} size={20} className="text-primary" />
                      </div>
                    </div>
                    
                    <div className="w-1/2"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-20 bg-gradient-to-br from-primary to-secondary relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl lg:text-4xl font-bold text-white">
                  Ready to Make an Impact?
                </h2>
                <p className="text-xl text-white/90 max-w-2xl mx-auto">
                  Let's discuss how my experience and growing expertise can benefit your team.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => window.location.href = '/contact'}
                  className="bg-white text-primary px-8 py-3 rounded-lg font-medium hover:bg-white/90 transition-all duration-200 transform hover:-translate-y-1 shadow-lg hover:shadow-xl"
                >
                  <Icon name="Mail" size={18} className="mr-2 inline" />
                  Get In Touch
                </button>
                <button
                  onClick={() => window.location.href = '/projects'}
                  className="border-2 border-white/30 text-white px-8 py-3 rounded-lg font-medium hover:bg-white/10 transition-all duration-200"
                >
                  <Icon name="FolderOpen" size={18} className="mr-2 inline" />
                  View Projects
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Experience; 